import React from "react"

import type { MetricRenderAdapter } from "@/metrics/runtime-adapters"

import type { RunRow, RunRowLike } from "@/types/RunRow"

const truncateString = (value: string, length: number) =>
  value.length > length ? `${value.substring(0, length)}...` : value

// Separate components for complex cells
const StringCell = ({ value }: { value: string }) => truncateString(value, 100)

function getTranscriptPreview(item: RunRowLike): string {
  return (
    (item as Partial<Pick<RunRow, "transcript">>).transcript?.[0]?.content ?? ""
  )
}

const MetricCell = ({
  columnKey,
  item,
  metricData,
}: {
  columnKey: string
  item: RunRowLike
  metricData: Record<string, MetricRenderAdapter>
}) => {
  const metricRecord = metricData[columnKey]
  if (!(metricRecord.id in item.metric_outputs) || !(columnKey in metricData)) {
    return <div className="text-center">-</div>
  }
  const mo = item.metric_outputs[metricRecord.id]
  if (!mo || !metricRecord) {
    return <div className="text-center">-</div>
  }
  return (
    <span className="font-mono tabular-nums flex items-center justify-center">
      {metricRecord.renderBadge(mo)}
    </span>
  )
}

// Define type for the renderer functions
type CellRenderer = (
  item: RunRowLike,
  metricData: Record<string, MetricRenderAdapter>,
  index: number,
) => React.ReactElement

const cellRenderers: Record<string, CellRenderer> = {
  row_number: (_, __, index) => <div>{index + 1}</div>,
  testSetRowId: (item) => <div>{item.test_set_row_id}</div>,
  inputStr: (item) => <StringCell value={item.input_str ?? ""} />,
  expectedBehaviors: (item) => (
    <StringCell value={item.expected_behaviors.join(" • ")} />
  ),
  transcript: (item) => <StringCell value={getTranscriptPreview(item)} />,
}

export const renderTableCell = (
  columnKey: string,
  item: RunRowLike,
  metricData: Record<string, MetricRenderAdapter>,
  index: number,
): React.ReactElement => {
  if (!item) {
    return <div>-</div>
  }

  // Get the specific renderer for this column
  const renderer = cellRenderers[columnKey]
  if (renderer) {
    return renderer(item, metricData, index)
  }

  // Handle metric columns
  if (metricData[columnKey]) {
    return (
      <MetricCell columnKey={columnKey} item={item} metricData={metricData} />
    )
  }

  // Default renderer for any other columns
  return <div>-</div>
}
